import {
  CSSProperties,
  useRef,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  memo,
} from 'react';
import { debounce } from 'lodash';
import { MicOff } from 'lucide-react';
import styled from 'styled-components';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import {
  TeamType,
  ParticipantPosition,
  ParticipantStatus,
} from '@typings/participant';
import UserNameBlock from '@components/show/card/components/UserNameBlock';
import { useParticipantDataById } from '@redux/selectors/participants';
import { useCurrentUser, useUserDataById } from '@redux/selectors/users';
import { PremierText } from '@components/text';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import animationData from '@assets/animations/heart.json';
import { formatNumber } from '@utils/index';
import { PARTICIPANT_NAME_HEIGHT, SKEW_ANGLE } from '@utils/constants';
import theme from 'theme';
import { useAuthUser } from '@redux/selectors/auth';

const Container = styled.div.attrs<{
  $isLeft?: boolean;
  $isRight?: boolean;
  $isLeftNeutral?: boolean;
  $skewOffset: number;
  $height: number;
  $width: number;
}>(({ $width, $skewOffset, $height, $isLeft, $isLeftNeutral }) => ({
  style: {
    width: `${$width + $skewOffset}px`,
    clipPath:
      $isLeft || $isLeftNeutral
        ? `polygon(
    0 0,
    ${$width + $skewOffset}px 0,
    ${$width}px ${$height}px,
    0 ${$height}px
  )`
        : `polygon(${$skewOffset}px 0, ${$width + $skewOffset}px 0, ${$width + $skewOffset}px ${$height}px, 0 ${$height}px)`,
  },
}))<{
  $isLeft?: boolean;
  $isRight?: boolean;
  $isLeftNeutral?: boolean;
  $skewOffset: number;
  $height: number;
  $width: number;
}>`
  display: flex;
  flex-direction: ${({ $isLeft, $isLeftNeutral }) =>
    $isLeft || $isLeftNeutral ? 'row' : 'row-reverse'};
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  ${({ $isLeft, $isLeftNeutral }) =>
    $isLeft || $isLeftNeutral ? 'left: 0;' : 'right: 0;'};
  background: ${({ $isLeft, $isRight, $isLeftNeutral, theme }) =>
    $isLeft
      ? `linear-gradient(90deg, ${theme.colors.blue[500]} 60%, ${theme.colors.purple[500]} 100%)`
      : $isRight
        ? `linear-gradient(90deg, ${theme.colors.purple[500]} 0%, ${theme.colors.red[500]} 60%)`
        : $isLeftNeutral
          ? `linear-gradient(100deg, ${theme.colors.grey[800]} 30%, ${theme.colors.grey[500]} 80%, ${theme.colors.grey[300]} 100%)`
          : `linear-gradient(80deg, ${theme.colors.grey[300]} 0%, ${theme.colors.grey[500]} 20%, ${theme.colors.grey[800]} 80%)`};
  gap: 8px;
  z-index: ${({ $isLeft, $isLeftNeutral }) =>
    $isLeft || $isLeftNeutral ? 3 : 2};
`;

const LikeButton = styled(TouchableOpacity)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
`;

const LikesCount = styled(PremierText)<{
  $isLeft?: boolean;
  $isDisabled?: boolean;
}>`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.white};
  order: ${({ $isLeft }) => ($isLeft ? 1 : 2)};
  padding-right: ${({ $isLeft, $isDisabled }) =>
    $isLeft && $isDisabled ? 8 : 0}px;
  padding-left: ${({ $isLeft, $isDisabled }) =>
    !$isLeft && $isDisabled ? 8 : 0}px;
`;

const LottieWrapper = styled.div<{ $isLeft?: boolean }>`
  order: ${({ $isLeft }) => ($isLeft ? 2 : 1)};
`;

const HeartAnimation = styled(Lottie)`
  width: 24px;
  height: 24px;
`;

const MicOffIcon = styled(MicOff)<{ $isLeft?: boolean }>`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.colors.white};
  order: ${({ $isLeft }) => ($isLeft ? 0 : 1)};
`;

const UserNameContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const UserName = styled(UserNameBlock)<{ $isLeft?: boolean }>`
  order: ${({ $isLeft }) => ($isLeft ? 1 : 0)};
  transform: skew(${SKEW_ANGLE}deg, 0);
`;

type Props = {
  side?: TeamType;
  width: number;
  position: ParticipantPosition;
  participantId: string | null;
  likesCount?: number;
  isLiked?: boolean;
  isLikeDisabled?: boolean;
  isStatusesShown?: boolean;
  onLike?: (isLiked: boolean) => void;
  style?: CSSProperties;
};

const SKEW_OFFSET = Math.ceil(PARTICIPANT_NAME_HEIGHT * 0.4);
const LIKE_DEBOUNCE = 800;

const NameContainer: FC<Props> = ({
  side,
  width,
  position,
  participantId,
  likesCount = 0,
  isLiked = false,
  isLikeDisabled,
  isStatusesShown,
  onLike,
}) => {
  const [localState, setLocalState] = useState({
    isLiked: isLiked,
    likesCount: likesCount,
  });

  const isLeft = useMemo(
    () => side === TeamType.Blue || position === ParticipantPosition.Left,
    [side, position],
  );
  const currentUser = useCurrentUser();
  const { isAnonymous } = useAuthUser();
  const participantData = useParticipantDataById(participantId);
  const profileData = useUserDataById(participantData?.profileId);
  const isMe = useMemo(
    () => !!currentUser?.id && participantData?.profileId === currentUser?.id,
    [participantData?.profileId, currentUser?.id],
  );
  const isMuted = useMemo(
    () =>
      participantData?.status &&
      (participantData?.status === ParticipantStatus.ForceMuted ||
        participantData?.status === ParticipantStatus.Muted),
    [participantData?.status],
  );
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);

  const setLike = useMemo(
    () =>
      debounce((liked: boolean) => {
        if (onLike) {
          onLike(liked);
        }
      }, LIKE_DEBOUNCE),
    [onLike],
  );

  const toggleLike = useCallback(() => {
    if (isAnonymous) {
      return;
    }

    setLocalState((prev) => {
      const newIsLiked = !prev.isLiked;
      const newLikesCount = newIsLiked
        ? prev.likesCount + 1
        : Math.max(prev.likesCount - 1, 0);
      setLike(newIsLiked);
      return {
        isLiked: newIsLiked,
        likesCount: newLikesCount,
      };
    });
  }, [setLike, isAnonymous]);

  useEffect(() => {
    setLocalState({
      isLiked,
      likesCount,
    });
  }, [isLiked, likesCount]);

  useEffect(() => {
    if (localState.isLiked) {
      lottieRef.current?.play();
    } else {
      lottieRef.current?.goToAndStop(1, true);
    }
  }, [localState.isLiked]);

  return (
    <Container
      $isLeft={side === TeamType.Blue}
      $isRight={side === TeamType.Red}
      $isLeftNeutral={position === ParticipantPosition.Left}
      $skewOffset={SKEW_OFFSET}
      $height={PARTICIPANT_NAME_HEIGHT}
      $width={width}
    >
      <UserNameContainer>
        {isMuted && isStatusesShown && <MicOffIcon $isLeft={isLeft} />}

        <UserName
          name={profileData?.name || ''}
          verifiedAs={profileData?.verifiedAs}
          $isLeft={isLeft}
          nameStyle={{
            fontSize: theme.fonts.fontSize.lg,
          }}
        />
      </UserNameContainer>

      {isStatusesShown && (
        <LikeButton
          onClick={toggleLike}
          disabled={isMe || isLikeDisabled || isAnonymous}
        >
          <LikesCount
            $isLeft={isLeft}
            $isDisabled={isMe || isLikeDisabled || isAnonymous}
          >
            {formatNumber(localState.likesCount)}
          </LikesCount>

          {!isMe && !isAnonymous ? (
            <LottieWrapper $isLeft={isLeft}>
              <HeartAnimation
                lottieRef={lottieRef}
                animationData={animationData}
                autoplay={false}
                loop={false}
              />
            </LottieWrapper>
          ) : null}
        </LikeButton>
      )}
    </Container>
  );
};

export default memo(NameContainer);
